import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Feather, BookOpen, Briefcase, MessageCircle, Heart, Star, Wallet, Bell,
  Sparkles, Settings as SettingsIcon, FileText, ShieldCheck, Award, Users, ArrowRight,
  Megaphone, Send,
} from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import MyActivity from "@/components/MyActivity";
import PrivateTesterAccessCard from "@/components/PrivateTesterAccessCard";
import ActionRequiredConfidentialityCard from "@/components/ActionRequiredConfidentialityCard";
import EarlyAccessLimitedBanner from "@/components/EarlyAccessLimitedBanner";
import EarlyAccessWelcomeBanner from "@/components/EarlyAccessWelcomeBanner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const roles = user?.roles || [];
  const hasBoth = roles.includes("author") && roles.includes("beta_reader");
  const initial = roles.includes("beta_reader") && !roles.includes("author") ? "reader" : "author";
  const [view, setView] = useState(initial);

  // If the user arrived with `#my-activity` (or clicked "Your Workspace" from
  // the nav), smooth-scroll the section into view once mounted. The tiny
  // timeout lets the MyActivity list first-paint before we scroll.
  useEffect(() => {
    if (location.hash === "#my-activity") {
      const t = setTimeout(() => {
        const el = document.getElementById("my-activity");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
    if (location.hash === "#community-update") {
      const t = setTimeout(() => {
        const el = document.getElementById("community-update");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
  }, [location.hash, location.key]);

  return (
    <SiteShell>
      <section className="container-page py-10 md:py-14" data-testid={TID.dashboard.root}>
        <TopWelcome view={view} />
        <EarlyAccessLimitedBanner context="dashboard" className="mb-6" />
        <Header user={user} />
        <OnboardingChecklist />

        {hasBoth && (
          <div className="mt-6 inline-flex rounded-full border p-1 bg-card" data-testid={TID.dashboard.switcher}>
            {["author", "reader"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`dashboard-view-${v}`}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  view === v
                    ? "bg-[hsl(var(--sb-forest))] text-white"
                    : "text-foreground/70 hover:bg-muted"
                }`}
              >
                {v === "author" ? "Author view" : "Beta Reader view"}
              </button>
            ))}
          </div>
        )}

        <ActionRequiredConfidentialityCard />

        <CommunityUpdateCard />

        <PrivateTesterAccessCard />

        <div className="mt-10">
          {view === "author"
            ? <AuthorGrid />
            : <ReaderGrid />}
        </div>

        <MyActivity />

        <ProfileCompleteness />
      </section>
    </SiteShell>
  );
}

function CommunityUpdateCard() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/community-updates/active");
        if (mounted) setUpdate(data?.update || null);
      } catch {
        // silent: card just doesn't show
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/community-updates/${update.id}/reply`,
        { body },
      );
      toast.success("Reply sent to Last Bookmark.");
      setReply("");
      setReplyOpen(false);
      // Deep-link into the thread so the user sees the full context.
      if (data?.thread_id) {
        window.location.assign(`/messages/${data.thread_id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (loading || !update) return null;

  return (
    <div
      id="community-update"
      className="mt-8 rounded-3xl border-2 border-[hsl(var(--sb-gold)/0.5)] bg-[hsl(var(--sb-cream-2)/0.6)] p-6 md:p-7 shadow-md scroll-mt-24"
      data-testid="dashboard-community-update-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[hsl(40_94%_32%)] dark:bg-[hsl(40_96%_28%)] grid place-items-center shrink-0 text-[hsl(42_60%_96%)] ring-1 ring-[hsl(var(--sb-gold-2))]/50 shadow-sm">
          <Megaphone size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] bg-[hsl(var(--sb-clay))] text-[hsl(40_60%_96%)] border border-[hsl(var(--sb-clay-2))]/60 shadow-sm dark:bg-[hsl(var(--sb-clay-2))] dark:text-[hsl(40_60%_96%)] dark:border-[hsl(var(--sb-clay))]/70"
              data-testid="dashboard-community-update-label"
            >
              From Last Bookmark
            </span>
            <span
              className="text-xs text-muted-foreground"
              data-testid="dashboard-community-update-date"
            >
              {new Date(update.date_posted).toLocaleDateString(undefined, {
                month: "short", day: "numeric", year: "numeric",
              })}
            </span>
          </div>
          <h2
            className="mt-2 font-serif text-2xl md:text-3xl tracking-tight text-foreground"
            data-testid="dashboard-community-update-title"
          >
            {update.title}
          </h2>
          <p
            className="mt-3 text-sm md:text-base whitespace-pre-wrap text-foreground/85 leading-relaxed"
            data-testid="dashboard-community-update-body"
          >
            {update.message}
          </p>
          {update.replies_enabled && !isAdmin && !user?.is_ephemeral && !replyOpen && (
            <button
              type="button"
              onClick={() => setReplyOpen(true)}
              className="mt-4 btn-primary"
              data-testid="dashboard-community-update-reply-open"
            >
              <MessageCircle size={16} /> Reply privately to Last Bookmark
            </button>
          )}
          {!update.replies_enabled && (
            <p className="mt-4 text-xs text-muted-foreground italic">
              Replies are turned off for this update.
            </p>
          )}
          {isAdmin && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/admin/community-updates"
                className="btn-ghost border text-xs"
                data-testid="dashboard-community-update-admin-manage"
              >
                Manage updates
              </Link>
            </div>
          )}
          {replyOpen && (
            <form onSubmit={send} className="mt-4 space-y-3" data-testid="dashboard-community-update-reply-form">
              <textarea
                className="sb-input w-full min-h-[100px] resize-y leading-relaxed"
                placeholder="Write a private reply to Last Bookmark…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                maxLength={4000}
                data-testid="dashboard-community-update-reply-input"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="btn-ghost border"
                  onClick={() => { setReplyOpen(false); setReply(""); }}
                  disabled={sending}
                  data-testid="dashboard-community-update-reply-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={sending || !reply.trim()}
                  data-testid="dashboard-community-update-reply-send"
                >
                  <Send size={16} /> {sending ? "Sending…" : "Send reply"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// "none" | "loading" | "show" | "has_jobs" | "retired" for approved authors.
function useFirstProjectInvite() {
  const { user } = useAuth();
  const [hasJobs, setHasJobs] = useState(null);
  const roles = user?.roles || [];
  const approvedAuthor = !!user && roles.includes("author") && !roles.includes("admin")
    && !!user.is_private_test && !user.is_ephemeral;
  const retired = approvedAuthor && !!user.first_project_invite_dismissed_at;

  useEffect(() => {
    if (!approvedAuthor || retired) return undefined;
    let mounted = true;
    (async () => {
      try {
        const [active, archived] = await Promise.all([
          api.get("/jobs", { params: { mine: true } }),
          api.get("/jobs", { params: { mine: true, archived: true } }),
        ]);
        const count = (active.data?.length || 0) + (archived.data?.length || 0);
        if (mounted) setHasJobs(count > 0);
      } catch {
        if (mounted) setHasJobs(true);
      }
    })();
    return () => { mounted = false; };
  }, [approvedAuthor, retired]);

  if (!approvedAuthor) return "none";
  if (retired) return "retired";
  if (hasJobs === null) return "loading";
  return hasJobs ? "has_jobs" : "show";
}

// Invite replaces the green welcome banner for approved authors with no projects.
function TopWelcome({ view }) {
  const status = useFirstProjectInvite();
  const [hidden, setHidden] = useState(false);
  if (view === "reader") return <EarlyAccessWelcomeBanner audience="reader" />;
  if (status === "none") return <EarlyAccessWelcomeBanner />;
  if (status === "loading" || status === "has_jobs") return null;
  if (status === "show" && !hidden) return <FirstProjectHint onHide={() => setHidden(true)} />;
  return null;
}

function FirstProjectHint({ onHide }) {
  const { refresh } = useAuth();
  const notNow = async () => {
    onHide();
    try {
      await api.post("/users/me/first-project-invite/dismiss", {});
      if (typeof refresh === "function") await refresh();
    } catch { /* local hide still applies this session */ }
  };

  return (
    <div className="mb-6 rounded-3xl border-2 border-[hsl(var(--sb-gold)/0.5)] bg-[hsl(var(--sb-cream-2)/0.5)] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6"
      data-testid="first-project-hint">
      <div className="w-12 h-12 rounded-full bg-[hsl(var(--sb-gold)/0.2)] dark:bg-[hsl(42_90%_68%/0.15)] dark:ring-1 dark:ring-[hsl(42_90%_68%/0.55)] grid place-items-center shrink-0" data-testid="first-project-hint-icon">
        <Feather size={22} className="text-[hsl(var(--sb-gold-2))] dark:text-[hsl(42_90%_68%)]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-serif text-xl sm:text-2xl" data-testid="first-project-hint-title">
          Your Story Deserves to Be Discovered! <span aria-hidden="true">📚</span>
        </h3>
        <div className="mt-2 space-y-2 text-sm text-foreground/80 leading-relaxed">
          <p>Ready to connect with beta readers? Last Bookmark has readers eager to discover new stories and provide thoughtful feedback.</p>
          <p>We&rsquo;ve also updated our confidentiality agreements to help protect authors and their work. Readers must review and acknowledge the confidentiality terms before accessing manuscripts.</p>
          <p>Whether your manuscript is finished or still a work in progress, you&rsquo;re welcome to create a project whenever you&rsquo;re ready!</p>
        </div>
        <div className="mt-4 flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <Link to="/jobs/new" className="btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="first-project-cta">
            Create Your First Project <ArrowRight size={16} />
          </Link>
          <button type="button" onClick={notNow} className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 min-h-[44px] px-2" data-testid="first-project-not-now">
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ user }) {
  const name = user?.profile?.display_name || user?.email;
  const roles = user?.roles || [];
  return (
    <div className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <span className="chip"><Sparkles size={14} /> Signed in</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight [overflow-wrap:anywhere]">
          Good to see you, {name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="mt-2 text-muted-foreground">
          {roles.includes("author") && roles.includes("beta_reader")
            ? "You're set up as both an author and a beta reader. Switch views anytime."
            : roles.includes("author")
            ? "Your author workspace — bring your next story to life."
            : "Your beta reader workspace — thoughtful feedback pays."}
        </p>
      </div>
      <Link to="/profile" className="btn-ghost border">
        Edit profile <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function AuthorGrid() {
  // "Writing Journey" is intentionally omitted while the underlying
  // feature isn't ready to ship. Underlying route/copy left in the
  // codebase for a future revive — no functionality removed.
  const tiles = [
    { icon: Briefcase, title: "Projects", body: "Post a beta read, invite readers, manage feedback.", stat: "Marketplace", cta: "Open projects", tid: TID.dashboard.tileProjects, to: "/jobs" },
    { icon: MessageCircle, title: "Messages", body: "Chat directly with beta readers and coffee-chat matches.", stat: "Live", cta: "Open inbox", tid: TID.dashboard.tileMessages, to: "/messages" },
    { icon: Heart, title: "Favorites", body: "Readers you'll come back to book after book.", stat: "Live", cta: "Open favorites", tid: TID.dashboard.tileFavorites, to: "/favorites" },
    { icon: Star, title: "Reviews", body: "Feedback across communication, thoroughness, professionalism and more.", stat: "5-category system", cta: "View reviews", tid: TID.dashboard.tileReviews, to: "/reviews" },
    { icon: FileText, title: "Portfolio", body: "Your bio, headline, genres, reviews and social links — the public profile beta readers see.", stat: "Editable", cta: "Update profile", tid: TID.dashboard.tilePortfolio, to: "/profile" },
    { icon: Wallet, title: "Payments", body: "Secure platform payments are planned for a future update. Payments are not currently processed by Last Bookmark.", stat: "Coming later", cta: "Not yet available", tid: TID.dashboard.tilePayments, comingSoon: true },
    { icon: Bell, title: "Notifications", body: "Applications, messages, favourites — all in one place.", stat: "0 new", cta: "See all", tid: TID.dashboard.tileNotifications, to: "/notifications" },
    { icon: SettingsIcon, title: "Settings", body: "Theme, accessibility, notifications and account.", stat: "", cta: "Open", tid: TID.dashboard.tileSettings, to: "/settings" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger-fade">
      {tiles.map((t, i) => <Tile key={t.title} {...t} variant={["deep","olive","warm"][i % 3]} />)}
    </div>
  );
}

function ReaderGrid() {
  const tiles = [
    { icon: Briefcase, title: "Available Jobs", body: "Beta read opportunities matching your genres.", stat: "Marketplace", cta: "Browse jobs", tid: TID.dashboard.tileAvailableJobs, to: "/jobs" },
    { icon: FileText, title: "Applications", body: "Track your applications and their status.", stat: "Live", cta: "See applications", tid: TID.dashboard.tileApplications, to: "/applications" },
    { icon: MessageCircle, title: "Messages", body: "Chat with authors before and during projects.", stat: "Live", cta: "Open inbox", tid: TID.dashboard.tileMessages + "-reader", to: "/messages" },
    { icon: BookOpen, title: "Portfolio", body: "Your bio, headline, genres, reading styles, certification badge and reviews — your public reader profile.", stat: "Editable", cta: "Update profile", tid: TID.dashboard.tilePortfolio + "-reader", to: "/profile" },
    { icon: Star, title: "Reviews", body: "Author feedback across communication, thoroughness, and more.", stat: "5-category", cta: "View reviews", tid: TID.dashboard.tileReviews + "-reader", to: "/reviews" },
    { icon: Wallet, title: "Earnings", body: "Integrated payouts to beta readers are planned for a future update. Payouts are not currently active during beta.", stat: "Not currently active", cta: "Not yet available", tid: TID.dashboard.tileEarnings, comingSoon: true },
    { icon: ShieldCheck, title: "Quality Score", body: "Your reliability rating across recent projects.", stat: "Live", cta: "View score", tid: TID.dashboard.tileQuality, to: "/community" },
    { icon: Award, title: "Certifications", body: "Apply for the Last Bookmark Certified badge.", stat: "Live", cta: "Apply", tid: TID.dashboard.tileCertifications, to: "/community" },
    { icon: Heart, title: "Favorites", body: "Authors whose next project you'd love to read.", stat: "Live", cta: "Open favorites", tid: TID.dashboard.tileFavorites + "-reader", to: "/favorites" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger-fade">
      {tiles.map((t, i) => <Tile key={t.title} {...t} variant={["deep","olive","warm"][i % 3]} />)}
    </div>
  );
}

function Tile({ icon: Icon, title, body, stat, cta, tid, tag, to, comingSoon, variant = "deep" }) {
  // Three visual rhythms so a page of tiles doesn't blur together — all now
  // on warm parchment cards so text reads clearly.
  //   deep    → forest-green icon on cream card
  //   olive   → sage-green icon on cream card
  //   warm    → amber icon on slightly deeper parchment
  const variants = {
    deep: {
      card: "bg-card border-border",
      iconWrap: "bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)]",
      title: "text-foreground",
      body: "text-muted-foreground",
      stat: "text-muted-foreground",
      cta: "text-[hsl(var(--sb-forest-2))]",
    },
    olive: {
      card: "bg-card border-border",
      iconWrap: "bg-[hsl(var(--sb-sage))] text-[hsl(var(--sb-forest-2))]",
      title: "text-foreground",
      body: "text-muted-foreground",
      stat: "text-muted-foreground",
      cta: "text-[hsl(var(--sb-forest-2))]",
    },
    warm: {
      card: "bg-[hsl(var(--sb-cream-2))] border-border",
      iconWrap: "bg-[hsl(var(--sb-gold-2))] text-[hsl(42_60%_96%)]",
      title: "text-foreground",
      body: "text-muted-foreground",
      stat: "text-muted-foreground",
      // Phase 46 — "Open favorites" CTA was too low-contrast on the coffee
      // card in dark mode (deep gold on medium-warm bg). Keep deep gold in
      // light mode, switch to a bright warm gold that pops on the coffee
      // card in dark mode. Palette family unchanged.
      cta: "text-[hsl(var(--sb-gold-2))] dark:text-[hsl(40_92%_82%)] font-bold underline-offset-2 group-hover:underline",
    },
  };
  const v = variants[variant] || variants.deep;
  const inner = (
    <article
      className={`rounded-2xl border p-6 shadow-md h-full flex flex-col group relative transition-all hover:-translate-y-0.5 hover:shadow-lg ${v.card} ${comingSoon ? "opacity-75" : ""}`}
      data-testid={tid}
      aria-disabled={comingSoon || undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`w-11 h-11 rounded-full grid place-items-center ${v.iconWrap}`}>
          <Icon size={20} />
        </div>
        {tag && (
          comingSoon ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--sb-gold))]/60 bg-[hsl(150_28%_10%)] text-[hsl(var(--sb-gold))] text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1"
              data-testid={`${tid}-coming-soon-badge`}
            >
              <Sparkles size={11} /> Coming soon · {tag}
            </span>
          ) : (
            <span
              className="inline-flex items-center rounded-full border border-[hsl(var(--sb-clay))]/60 bg-[hsl(150_28%_10%)] text-[hsl(var(--sb-clay))] text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1"
            >
              {tag}
            </span>
          )
        )}
      </div>
      <h3 className={`mt-5 font-serif text-xl ${v.title}`}>{title}</h3>
      <p className={`mt-2 text-sm ${v.body} leading-relaxed flex-1`}>{body}</p>
      <div className="mt-5 flex items-center justify-between">
        <span className={`text-xs ${v.stat}`}>{stat}</span>
        <span
          className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
            comingSoon
              ? "text-muted-foreground italic"
              : `${v.cta} group-hover:gap-2 transition-all`
          }`}
        >
          {cta} {!comingSoon && <ArrowRight size={14} />}
        </span>
      </div>
    </article>
  );
  if (comingSoon) return inner;
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}

function ProfileCompleteness() {
  const { user } = useAuth();
  const p = user?.profile || {};
  const percent = useMemo(() => {
    const checks = [
      p.display_name,
      p.headline,
      p.bio,
      p.avatar_url,
      p.genres?.length,
      p.languages?.length,
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [p]);
  if (percent === 100) return null;
  return (
    <div className="mt-12 card-soft flex flex-col sm:flex-row items-start sm:items-center gap-6">
      <div className="flex-1">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <Users size={14} /> Grow your presence
        </div>
        <h3 className="mt-2 font-serif text-2xl">Your profile is {percent}% complete.</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete profiles get invited to projects sooner. It takes about 3 minutes.
        </p>
      </div>
      <div className="w-full sm:w-64">
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-[hsl(var(--sb-clay))] transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
        <Link to="/profile" className="mt-4 btn-primary w-full">Complete profile</Link>
      </div>
    </div>
  );
}
